import {
  FieldWrapper,
  InputSelectionList,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { Controller, FieldValues } from 'react-hook-form';
import { StrictOmit } from 'ts-essentials';

import { getFieldMeta } from '../../utils';
import { FieldInputTextProps } from './fieldInputText';

export type ListItem = {
  id: string;
  name: string;
};

export type FieldSelectionListProps<T extends FieldValues> = StrictOmit<
  FieldInputTextProps<T>,
  'placeholder'
> & {
  itemsLoading: boolean;
  placeholders: {
    loading: string;
    empty: string;
  };
  equalityFn: (a: ListItem, b: ListItem) => boolean;
  listItems: ListItem[];
  multiple?: boolean;
};
/** React Hook Form input component wrapped within a Habla FieldWrapper */
export function FieldSelectionList<T extends FieldValues>({
  control,
  name,
  label,
  required,
  description,
  itemsLoading,
  equalityFn,
  placeholders,
  listItems,
  multiple = true,
  disabled,
}: FieldSelectionListProps<T>): JSX.Element {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const meta = getFieldMeta(fieldState);
        return (
          <FieldWrapper
            label={label}
            required={required}
            meta={meta}
            description={description}
          >
            <InputSelectionList
              multiple={multiple}
              value={field.value}
              onChange={(fieldValue) =>
                field.onChange(fieldValue.selectedValues)
              }
              disabled={itemsLoading || disabled}
              placeholder={
                itemsLoading ? placeholders.loading : placeholders.empty
              }
              equalityFn={equalityFn}
              getItemTitleByValue={getItemTitle}
              selectionListProps={{
                style: { maxHeight: 300, overflow: 'auto' },
              }}
            >
              {(listItems || []).map((queue) => (
                <SelectionList.Item key={queue.id} value={queue}>
                  <Text.Paragraph>{queue.name}</Text.Paragraph>
                </SelectionList.Item>
              ))}
            </InputSelectionList>
          </FieldWrapper>
        );
      }}
    />
  );
}

export function getItemTitle(
  itemValue: ListItem | null | undefined,
): React.ReactNode {
  return <>{itemValue?.name}</>;
}
